/**
 * src/views/focus/DashboardSection.jsx — KPI cards, AI Focus, Team Workload, Active Projects
 * Extracted from FocusView.jsx
 *
 * Renamed single-letter variables:
 *   G  → GlassCard (local)
 *   Av → AvatarInitials
 *   PB → PriorityBadge
 *   SC → StatusChip (unused here, but renamed for consistency)
 *   Bar → ProgressBar
 *   td → today
 */

import { useState } from 'react';
import { AlertCircle, Clock, BarChart3, Target, Sparkles, UserCircle, ChevronRight, ChevronDown, X, CalendarDays } from 'lucide-react';
import RoadblockRadar from './RoadblockRadar';
import { useT } from '../../lib/i18n/I18nContext';
import useBreakpoint from '../../hooks/useBreakpoint';
import { isDone, isTodo, isInProgress, isBlocked } from '../../lib/constants/taskStatus';
import { GlassCard } from '../../components/SharedUI';

/* ═══ COLLAPSIBLE SECTION (mobile progressive disclosure) ═══ */
function CollapsibleSection({ title, icon: Icon, count, defaultOpen = true, isMobile, children }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!isMobile) return <>{children}</>;
  return (
    <div style={{ marginBottom: 16 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '10px 0', background: 'none', cursor: 'pointer',
          borderBottom: '1px solid var(--bdr)',
        }}
      >
        {Icon && <Icon size={14} color="var(--accent)" />}
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t2)', flex: 1, textAlign: 'left' }}>{title}</span>
        {count != null && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', background: 'var(--stodob)', padding: '2px 8px', borderRadius: 99 }}>{count}</span>}
        <ChevronDown size={14} style={{ color: 'var(--tm)', transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 200ms' }} />
      </button>
      {open && <div style={{ marginTop: 8, animation: 'fadeIn 0.2s' }}>{children}</div>}
    </div>
  );
}

/* ═══ DATA ═══ */
const PRIORITIES = {
  P1: { c: "var(--p1)", b: "var(--p1b)", r: "var(--bad)" },
  P2: { c: "var(--p2)", b: "var(--p2b)", r: "var(--warn)" },
  P3: { c: "var(--p3)", b: "var(--p3b)", r: "var(--ok)" },
};

const today = () => new Date().toISOString().slice(0, 10);

/* ═══ ATOMS ═══ */
const AvatarInitials = ({ name, size = 32, color }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%",
    background: color || "var(--navy-surface)", color: "white",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.36, fontWeight: 600, flexShrink: 0,
  }}>{name.split(" ").map(w => w[0]).join("").slice(0, 2)}</div>
);

const PriorityBadge = ({ level }) => {
  const p = PRIORITIES[level];
  return p ? (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      fontSize: 11, fontWeight: 600, color: p.c,
      background: p.b, padding: "3px 8px", borderRadius: 99,
      letterSpacing: "0.03em",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: p.c }} />
      {level}
    </span>
  ) : null;
};

const ProgressBar = ({ pct, color }) => (
  <div style={{ height: 4, background: "var(--bdr)", borderRadius: 2, overflow: "hidden" }}>
    <div style={{
      height: "100%", borderRadius: 2,
      background: color || "var(--accent)",
      width: `${pct}%`,
      transition: "width 0.6s var(--ease)",
    }} />
  </div>
);

/* ═══ GAUGE (Apple Watch-style ring) ═══ */
function Gauge({ value, max, label, sub, color, icon: Icon, onClick }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const r = 50, cx = 60, cy = 60, sw = 10;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div
      onClick={onClick} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); } }}
      className={`text-center flex-1 min-w-0 ${onClick ? 'cursor-pointer' : ''}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${label}: ${value}` : undefined}
    >
      <svg viewBox="0 0 120 120" className="block mx-auto w-[90px] h-[90px] md:w-[110px] md:h-[110px]"
        role="img" aria-label={`${label}: ${value}${sub ? ` (${sub})` : ''}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--brd2,var(--bdr))" strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeDasharray={`${dash} ${circ}`} strokeDashoffset={-circ * 0.25}
          strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.8s var(--ease,ease)' }} />
        {Icon && <><foreignObject x={cx - 8} y={16} width={16} height={16} style={{ overflow: 'visible' }}>
          <Icon size={14} strokeWidth={1.5} color="var(--tm)" />
        </foreignObject></>}
        <text x={cx} y={cy + 2} textAnchor="middle" fill="var(--t1)" fontSize="20" fontWeight="800">{value}</text>
        {sub && <text x={cx} y={cy + 16} textAnchor="middle" fill="var(--tm)" fontSize="9">{sub}</text>}
      </svg>
      <div className="text-[11px] text-t2 mt-1 font-semibold flex items-center justify-center gap-1">
        <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
        {label}
      </div>
    </div>
  );
}

/* ═══ AI RANKING ENGINE ═══ */
function aiRank(tasks) {
  return tasks.filter(t => !isDone(t.st)).map(t => {
    let score = 0;
    const reasons = [];
    if (t.due && t.due <= today()) { score += 120; reasons.push("Overdue"); }
    if (t.pr === "P1") { score += 100; reasons.push("Critical"); }
    if (isBlocked(t.st)) { score += 80; reasons.push("Blocked"); }
    if (t.due && t.due === today()) { score += 60; reasons.push("Due today"); }
    if (t.pr === "P2") score += 50;
    if (t.area === "Finances") { score += 20; reasons.push("Revenue impact"); }
    if (t.area === "Procurement") { score += 15; reasons.push("Supply chain"); }
    if (!reasons.length) reasons.push(t.pr === "P2" ? "Medium priority" : "Normal");
    return { ...t, score, why: reasons.join(" · ") };
  }).sort((a, b) => b.score - a.score);
}

/**
 * DashboardSection — KPI gauges, AI Focus, Team Workload, Active Projects
 */
export default function DashboardSection({
  tasks, open, projects, people, rawTasks,
  onSelectTask, onOpenModal,
}) {
  const { t } = useT();
  const { isMobile } = useBreakpoint();
  const [drilldown, setDrilldown] = useState(null); // 'completion' | 'overdue' | 'blocked' | 'projects'

  const counts = { P1: 0, P2: 0, P3: 0, over: 0, blk: 0 };
  open.forEach(t => {
    counts[t.pr]++;
    if (isBlocked(t.st)) counts.blk++;
    if (t.due && t.due < today()) counts.over++;
  });

  const top3 = aiRank(tasks).slice(0, 3);

  // FIX[shape]: people are flat snake_case rows
  const teamPeople = people.slice(0, 10).map(p => ({
    name: p.display_name || '',
    role: p.role || '',
    color: 'var(--navy)',
    id: p.id,
  })).filter(p => p.name);

  // Drilldown data
  const overdueTasks = open.filter(t => t.due && t.due < today());
  const blockedTasks = open.filter(t => isBlocked(t.st));

  const toggleDrilldown = (key) => setDrilldown(prev => prev === key ? null : key);

  return (
    <div style={{ padding: 16 }}>
      {/* Gauge Row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, justifyContent: "center", flexWrap: "wrap", animation: "fadeUp 0.3s var(--ease)" }}>
        <Gauge value={tasks.filter(t => isDone(t.st)).length} max={tasks.length || 1} label={t('focus.gaugeCompletion')} sub={`${tasks.filter(t => isDone(t.st)).length}/${tasks.length}`} color="var(--ok)" icon={Target} onClick={() => toggleDrilldown('completion')} />
        <Gauge value={counts.over} max={open.length || 1} label={t('focus.gaugeOverdue')} sub={counts.over > 0 ? t('focus.needsAttention') : t('focus.allOnTrack')} color="var(--bad)" icon={AlertCircle} onClick={() => toggleDrilldown('overdue')} />
        <Gauge value={counts.blk} max={open.length || 1} label={t('focus.gaugeBlocked')} sub={`${counts.blk} ${counts.blk !== 1 ? t('common.tasks') : t('common.task')}`} color="var(--warn,#DD6B20)" icon={Clock} onClick={() => toggleDrilldown('blocked')} />
        <Gauge value={projects.length} max={Math.max(projects.length, 10)} label={t('projects.title')} sub={`${projects.length} ${t('focus.activeBadge')}`} color="var(--accent)" icon={BarChart3} onClick={() => toggleDrilldown('projects')} />
      </div>

      {/* ── Drill-down Panel ── */}
      {drilldown && (
        <div className="rounded-xl border border-bdr bg-card mt-2 mb-4 animate-[fadeUp_0.2s_ease]" style={{ position: 'relative', padding: '16px 16px 12px' }}>
          <button onClick={() => setDrilldown(null)} className="absolute top-3 right-3 w-11 h-11 rounded-full flex items-center justify-center bg-glass border border-bdr cursor-pointer text-tm hover:text-t1 transition-colors">
            <X size={14} />
          </button>

          {drilldown === 'completion' && (
            <div>
              <div className="text-[13px] font-bold text-t1 mb-3">{t('focus.taskCompletion')}</div>
              <div className="flex gap-4 mb-3 text-[12px]">
                <span className="text-ok font-semibold">{tasks.filter(t => isDone(t.st)).length} {t('status.done')}</span>
                <span className="text-spro font-semibold">{tasks.filter(t => isInProgress(t.st)).length} {t('status.inProgress')}</span>
                <span className="text-stodo font-semibold">{tasks.filter(t => isTodo(t.st)).length} {t('status.todo')}</span>
              </div>
              {tasks.filter(t => !isDone(t.st)).slice(0, 8).map(t => (
                <div key={t.id} role="button" tabIndex={0} onClick={() => { const raw = rawTasks.find(r => r.id === t.id); if (raw) onSelectTask(raw); else onOpenModal(t); }} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const raw = rawTasks.find(r => r.id === t.id); if (raw) onSelectTask(raw); else onOpenModal(t); } }} className="flex items-center gap-2 border-b border-bdr cursor-pointer hover:bg-glass transition-colors" style={{ minHeight: 48, padding: '8px 0' }}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: isInProgress(t.st) ? 'var(--spro)' : isBlocked(t.st) ? 'var(--sblk)' : 'var(--stodo)' }} />
                  <span className="text-[13px] text-t1 flex-1 truncate">{t.title}</span>
                  <span className="text-[11px] text-tm">{t.st}</span>
                </div>
              ))}
            </div>
          )}

          {drilldown === 'overdue' && (
            <div>
              <div className="text-[13px] font-bold text-t1 mb-3">{t('focus.overdueTasks')}</div>
              {overdueTasks.length === 0 ? (
                <div className="text-[13px] text-tm py-2">{t('focus.allTasksOnTrack')}</div>
              ) : overdueTasks.map(t => (
                <div key={t.id} role="button" tabIndex={0} onClick={() => { const raw = rawTasks.find(r => r.id === t.id); if (raw) onSelectTask(raw); else onOpenModal(t); }} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const raw = rawTasks.find(r => r.id === t.id); if (raw) onSelectTask(raw); else onOpenModal(t); } }} className="flex items-center gap-2 border-b border-bdr cursor-pointer hover:bg-glass transition-colors" style={{ minHeight: 48, padding: '8px 0' }}>
                  <AlertCircle size={12} className="text-bad shrink-0" />
                  <span className="text-[13px] text-t1 flex-1 truncate">{t.title}</span>
                  <span className="text-[11px] text-bad font-semibold flex items-center gap-1"><CalendarDays size={10} />{t.due}</span>
                </div>
              ))}
            </div>
          )}

          {drilldown === 'blocked' && (
            <div>
              <div className="text-[13px] font-bold text-t1 mb-3">{t('focus.blockedTasks')}</div>
              {blockedTasks.length === 0 ? (
                <div className="text-[13px] text-tm py-2">{t('focus.noBlockedTasks')}</div>
              ) : blockedTasks.map(t => (
                <div key={t.id} role="button" tabIndex={0} onClick={() => { const raw = rawTasks.find(r => r.id === t.id); if (raw) onSelectTask(raw); else onOpenModal(t); }} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const raw = rawTasks.find(r => r.id === t.id); if (raw) onSelectTask(raw); else onOpenModal(t); } }} className="flex items-center gap-2 border-b border-bdr cursor-pointer hover:bg-glass transition-colors" style={{ minHeight: 48, padding: '8px 0' }}>
                  <Clock size={12} className="text-warn shrink-0" />
                  <span className="text-[13px] text-t1 flex-1 truncate">{t.title}</span>
                  {t.who && <span className="text-[11px] text-tm flex items-center gap-1"><UserCircle size={10} />{t.who}</span>}
                </div>
              ))}
            </div>
          )}

          {drilldown === 'projects' && (
            <div>
              <div className="text-[13px] font-bold text-t1 mb-3">{t('focus.activeProjects')}</div>
              {projects.map(p => {
                // FIX[shape]: project rows are flat snake_case
                const name = p.project_name || '';
                const num = p.project_number || '';
                const pct = p.completion_pct || 0;
                return (
                  <div key={p.id} className="flex items-center gap-3 border-b border-bdr" style={{ minHeight: 48, padding: '10px 0' }}>
                    <span className="text-[13px] text-t1 flex-1 truncate">{num ? `#${num} - ` : ''}{name}</span>
                    <span className="text-[12px] font-bold text-accent shrink-0">{pct}%</span>
                    <div className="w-16 h-1.5 rounded-full bg-bdr overflow-hidden shrink-0"><div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(pct, 2)}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* On mobile: AI Focus FIRST (most actionable), then collapsible sections */}
      {/* On desktop: keep original order (Roadblock → AI Focus) */}

      {!isMobile && (
        <div style={{ marginBottom: 16, animation: "fadeUp 0.3s var(--ease) 0.08s both" }}><RoadblockRadar /></div>
      )}

      {/* AI Focus — always visible, first on mobile */}
      <GlassCard style={{ padding: 18, marginBottom: 16, background: "linear-gradient(135deg,rgba(28,63,117,0.06),rgba(0,48,135,0.03))", border: "1px solid rgba(28,63,117,0.12)", animation: "fadeUp 0.3s var(--ease) 0.1s both" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,var(--navy-surface),var(--blue))", display: "flex", alignItems: "center", justifyContent: "center" }}><Sparkles size={16} style={{ color: "white" }} /></div>
          <div><div style={{ fontSize: 14, fontWeight: 700, color: "var(--accent)" }}>{t('focus.aiFocus')}</div></div>
        </div>
        {top3.length === 0 && <div style={{ padding: "12px 0", fontSize: 13, color: "var(--tm)" }}>{t('focus.noOpenTasks')}</div>}
        {top3.map((t, i) => (
          <div key={t.id} role="button" tabIndex={0} onClick={() => {
            const raw = rawTasks.find(r => r.id === t.id);
            if (raw) onSelectTask(raw);
            else onOpenModal(t);
          }} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const raw = rawTasks.find(r => r.id === t.id); if (raw) onSelectTask(raw); else onOpenModal(t); } }} style={{ display: "flex", gap: 16, padding: "12px 0", borderTop: i ? "1px solid var(--bdr)" : "none", cursor: "pointer", alignItems: "flex-start" }}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-extrabold shrink-0" style={{ background: PRIORITIES[t.pr]?.b || PRIORITIES.P2.b, color: PRIORITIES[t.pr]?.c || PRIORITIES.P2.c }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4, marginBottom: 4 }}>{t.title}</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}><PriorityBadge level={t.pr} /><span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 500 }}>{t.why}</span></div>
              {t.who && <div style={{ fontSize: 11, color: "var(--tm)", marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}><UserCircle size={11} />{t.who}</div>}
            </div>
            <ChevronRight size={16} style={{ color: "var(--tm)", marginTop: 4, flexShrink: 0 }} />
          </div>
        ))}
      </GlassCard>

      {/* Roadblock Radar — on mobile, appears after AI Focus, collapsible */}
      {isMobile && (
        <CollapsibleSection title={t('focus.roadblockRadar') || 'Roadblock Radar'} icon={AlertCircle} count={counts.blk + counts.over} defaultOpen={false} isMobile={isMobile}>
          <RoadblockRadar />
        </CollapsibleSection>
      )}

      {/* Team Workload Mini */}
      {teamPeople.length > 0 && <CollapsibleSection title={t('focus.teamWorkload')} icon={UserCircle} count={teamPeople.length} defaultOpen={!isMobile} isMobile={isMobile}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {teamPeople.map(person => {
            const activeTasks = open.filter(t => t.who === person.name || t.whoId === person.id).length;
            const blockedTasks = open.filter(t => (t.who === person.name || t.whoId === person.id) && isBlocked(t.st)).length;
            return (
              <GlassCard key={person.id} style={{ padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <AvatarInitials name={person.name} size={28} color={person.color} />
                  <div><div style={{ fontSize: 13, fontWeight: 600 }}>{person.name}</div><div style={{ fontSize: 11, color: "var(--tm)" }}>{person.role}</div></div>
                </div>
                <ProgressBar pct={Math.min(activeTasks * 25, 100)} color={activeTasks >= 4 ? "var(--bad)" : activeTasks >= 3 ? "var(--warn)" : "var(--accent)"} />
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTasks > 0 ? 'text-accent bg-accent-bg' : 'text-tm bg-stodob'}`}>{activeTasks} {t('focus.activeBadge')}</span>
                  {blockedTasks > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: "var(--sblk)", background: "var(--sblkb)", padding: "2px 6px", borderRadius: 99 }}>{blockedTasks} {t('focus.blockedBadge')}</span>}
                </div>
              </GlassCard>
            );
          })}
        </div>
      </CollapsibleSection>}

      {/* Active Projects */}
      {projects.length > 0 && <CollapsibleSection title={t('focus.activeProjects')} icon={BarChart3} count={projects.length} defaultOpen={!isMobile} isMobile={isMobile}>
        {projects.map(p => {
          // FIX[shape]: project rows are flat snake_case with joined relations
          const name = p.project_name || '';
          const vessel = p.vessels?.vessel_name || '';
          const stage = p.stages?.label_en || p.stages?.stage_name || '';
          const pct = p.completion_pct || 0;
          return (
            <GlassCard key={p.id} style={{ padding: "12px 16px", marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div><div style={{ fontSize: 14, fontWeight: 500 }}>{name}</div><div style={{ fontSize: 11, color: "var(--tm)" }}>{vessel}{vessel && stage ? ' · ' : ''}{stage}</div></div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--accent)" }}>{pct}%</span>
              </div>
              <ProgressBar pct={pct} color="var(--accent)" />
            </GlassCard>
          );
        })}
      </CollapsibleSection>}
    </div>
  );
}
